import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import {
  IdeaCandidateSchema,
  IdeaCardSchema,
  IdeaDetailSchema,
  IdeaSchema,
  type Idea,
  type IdeaCandidate,
  type IdeaCard,
  type IdeaDetail,
  type IdeaEnsure,
  type IdeaEntityType,
  type IdeaListQuery,
} from "#shared/schemas/idea.ts";
import {
  ResearchCategorySchema,
  ResearchPromptResponseSchema,
  type ResearchCategory,
  type ResearchPromptMode,
} from "#shared/schemas/research.ts";
import { buildResearchFixPrompt } from "#shared/researchDefaults.ts";
import { z } from "zod";
import { notifyError } from "@web/features/diagnostics/notifyError.ts";
import { resolveError } from "@web/features/diagnostics/resolveError.ts";
import { api, ApiRequestError } from "@web/lib/api.ts";

const CandidatesResponse = z.object({
  candidates: z.array(IdeaCandidateSchema),
});
const IdeaResponse = z.object({ idea: IdeaSchema });
const IdeaDetailResponse = z.object({ idea: IdeaDetailSchema });
const CardResponse = z.object({ card: IdeaCardSchema });
const CategoriesResponse = z.object({
  categories: z.array(ResearchCategorySchema),
});

function failToast(err: unknown, fallback: string) {
  notifyError(err, {
    fallbackCode: /json|research/i.test(fallback) ? "RESEARCH_JSON_INVALID" : "SAVE_FAILED",
    actionLabel: fallback,
  });
}

function listQueryString(params: IdeaListQuery): string {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.playlist_id) qs.set("playlist_id", params.playlist_id);
  if (params.local_playlist_id) qs.set("local_playlist_id", params.local_playlist_id);
  if (params.fact) qs.set("fact", params.fact);
  if (params.used) qs.set("used", params.used);
  if (params.cards && params.cards !== "all") qs.set("cards", params.cards);
  if (params.limit) qs.set("limit", String(params.limit));
  return qs.toString();
}

export function useIdeaCandidates(params: IdeaListQuery) {
  const qs = listQueryString(params);
  return useQuery({
    queryKey: ["ideas", "candidates", params],
    queryFn: async (): Promise<IdeaCandidate[]> => {
      const body = CandidatesResponse.parse(await api(`/api/ideas?${qs}`));
      return body.candidates;
    },
  });
}

export function useIdeaDetail(id: string | undefined) {
  return useQuery({
    queryKey: ["ideas", id],
    enabled: Boolean(id),
    queryFn: async (): Promise<IdeaDetail> => {
      const body = IdeaDetailResponse.parse(await api(`/api/ideas/${id}`));
      return body.idea;
    },
  });
}

export function useEnsureIdea() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (input: IdeaEnsure): Promise<Idea> => {
      const body = IdeaResponse.parse(
        await api("/api/ideas/ensure", { method: "POST", json: input }),
      );
      return body.idea;
    },
    onSuccess: (idea) => {
      void qc.invalidateQueries({ queryKey: ["ideas"] });
      void navigate(`/ideas/${encodeURIComponent(idea.id)}`);
    },
    onError: (err) => failToast(err, "Could not start idea"),
  });
}

export function useUpdateIdea(ideaId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: { title?: string; used?: boolean }): Promise<Idea> => {
      const body = IdeaResponse.parse(
        await api(`/api/ideas/${ideaId}`, { method: "PATCH", json: patch }),
      );
      return body.idea;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ideas"] });
    },
    onError: (err) => failToast(err, "Could not update idea"),
  });
}

export function useIngestResearch(ideaId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (pasted: string): Promise<IdeaDetail> => {
      const body = IdeaDetailResponse.parse(
        await api(`/api/ideas/${ideaId}/research/ingest`, {
          method: "POST",
          json: { pasted },
        }),
      );
      return body.idea;
    },
    onSuccess: (idea) => {
      qc.setQueryData(["ideas", ideaId], idea);
      void qc.invalidateQueries({ queryKey: ["ideas", "candidates"] });
      void qc.invalidateQueries({ queryKey: ["ideas", "research", "categories"] });
      toast.success("Research ingested");
    },
    onError: (err) => {
      const isResearchShape =
        err instanceof ApiRequestError &&
        (err.code === "research_validation" ||
          err.code === "research_bad_json" ||
          err.code === "research_no_json" ||
          /json|research|schema|claim/i.test(err.message));
      if (!isResearchShape) {
        failToast(err, "Could not ingest research");
        return;
      }
      const resolved = resolveError(err, "RESEARCH_JSON_INVALID");
      toast.error(resolved.message, {
        description: resolved.errorId ? `Ref ${resolved.errorId}` : undefined,
        duration: 12_000,
        action: {
          label: "Copy fix prompt",
          onClick: () => {
            void navigator.clipboard
              .writeText(buildResearchFixPrompt(resolved.message))
              .then(() => {
                toast.success("Fix prompt copied — paste into your AI chat");
              })
              .catch(() => {
                toast.error("Could not copy fix prompt");
              });
          },
        },
      });
    },
  });
}

export function useDismissResearchNote(ideaId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ingestId: string): Promise<IdeaDetail> => {
      const body = IdeaDetailResponse.parse(
        await api(`/api/ideas/${ideaId}/research/notes/${ingestId}/dismiss`, {
          method: "POST",
        }),
      );
      return body.idea;
    },
    onSuccess: (idea) => {
      qc.setQueryData(["ideas", ideaId], idea);
    },
    onError: (err) => failToast(err, "Could not dismiss note"),
  });
}

export function useCreateIdeaCard(ideaId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      body: string;
      category?: string | null;
      after_id?: string | null;
      before_id?: string | null;
    }): Promise<IdeaCard> => {
      const res = CardResponse.parse(
        await api(`/api/ideas/${ideaId}/cards`, {
          method: "POST",
          json: input,
        }),
      );
      return res.card;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ideas", ideaId] });
      void qc.invalidateQueries({ queryKey: ["ideas", "candidates"] });
      void qc.invalidateQueries({ queryKey: ["ideas", "research", "categories"] });
    },
    onError: (err) => failToast(err, "Could not add card"),
  });
}

export function useUpdateIdeaCard(ideaId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      cardId: string;
      body?: string;
      category?: string | null;
      after_id?: string | null;
      before_id?: string | null;
    }): Promise<IdeaCard> => {
      const { cardId, ...patch } = input;
      const res = CardResponse.parse(
        await api(`/api/ideas/${ideaId}/cards/${cardId}`, {
          method: "PATCH",
          json: patch,
        }),
      );
      return res.card;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ideas", ideaId] });
      void qc.invalidateQueries({ queryKey: ["ideas", "research", "categories"] });
    },
    onError: (err) => failToast(err, "Could not update card"),
  });
}

export function useDeleteIdeaCard(ideaId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (cardId: string): Promise<void> => {
      await api(`/api/ideas/${ideaId}/cards/${cardId}`, { method: "DELETE" });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ideas", ideaId] });
      void qc.invalidateQueries({ queryKey: ["ideas", "candidates"] });
    },
    onError: (err) => failToast(err, "Could not delete card"),
  });
}

export function useDuplicateIdeaCard(ideaId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (cardId: string): Promise<IdeaCard> => {
      const res = CardResponse.parse(
        await api(`/api/ideas/${ideaId}/cards/${cardId}/duplicate`, {
          method: "POST",
        }),
      );
      return res.card;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ideas", ideaId] });
      toast.success("Card duplicated");
    },
    onError: (err) => failToast(err, "Could not duplicate card"),
  });
}

export function useReorderIdeaCard(ideaId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      cardId: string;
      after_id?: string | null;
      before_id?: string | null;
    }): Promise<IdeaCard> => {
      const { cardId, ...patch } = input;
      const res = CardResponse.parse(
        await api(`/api/ideas/${ideaId}/cards/${cardId}`, {
          method: "PATCH",
          json: patch,
        }),
      );
      return res.card;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ideas", ideaId] });
    },
    onError: (err) => failToast(err, "Could not reorder card"),
  });
}

export function useCopyResearchPrompt(
  ideaId: string | undefined,
  mode: ResearchPromptMode = "initial",
) {
  return useMutation({
    mutationFn: async (): Promise<string> => {
      if (!ideaId) throw new Error("Missing idea");
      const qs = new URLSearchParams({ mode });
      const body = ResearchPromptResponseSchema.parse(
        await api(`/api/ideas/${ideaId}/research/prompt?${qs}`),
      );
      await navigator.clipboard.writeText(body.prompt);
      return body.prompt;
    },
    onSuccess: () => {
      toast.success(
        mode === "continue"
          ? "Continue research prompt copied — paste into your AI chat"
          : "Research prompt copied — paste into your AI chat",
      );
    },
    onError: (err) => failToast(err, "Could not copy research prompt"),
  });
}

export function useCopyEntityResearchPrompt() {
  return useMutation({
    mutationFn: async (input: {
      entity_type: "track" | "album" | "artist";
      entity_id: string;
    }): Promise<string> => {
      const qs = new URLSearchParams(input);
      const body = ResearchPromptResponseSchema.parse(
        await api(`/api/ideas/research/prompt?${qs}`),
      );
      await navigator.clipboard.writeText(body.prompt);
      return body.prompt;
    },
    onSuccess: () => {
      toast.success("Research prompt copied — paste into your AI chat");
    },
    onError: (err) => failToast(err, "Could not copy research prompt"),
  });
}

export function useResearchCategories() {
  return useQuery({
    queryKey: ["ideas", "research", "categories"],
    queryFn: async (): Promise<ResearchCategory[]> => {
      const body = CategoriesResponse.parse(await api("/api/ideas/research/categories"));
      return body.categories;
    },
  });
}

export function useResearchTemplates() {
  return useQuery({
    queryKey: ["ideas", "research", "template"],
    queryFn: async (): Promise<{ template: string; continue_template: string }> => {
      return z
        .object({
          template: z.string(),
          continue_template: z.string(),
        })
        .parse(await api("/api/ideas/research/template"));
    },
  });
}

/** @deprecated prefer useResearchTemplates — kept for call sites that only need initial. */
export function useResearchTemplate() {
  const q = useResearchTemplates();
  return {
    ...q,
    data: q.data?.template,
  };
}

export function useSaveResearchTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (template: string): Promise<string> => {
      const body = z
        .object({ template: z.string() })
        .parse(
          await api("/api/ideas/research/template", {
            method: "PUT",
            json: { template },
          }),
        );
      return body.template;
    },
    onSuccess: (template) => {
      qc.setQueryData(
        ["ideas", "research", "template"],
        (prev: { template: string; continue_template: string } | undefined) =>
          prev
            ? { ...prev, template }
            : { template, continue_template: "" },
      );
      toast.success("Initial research template saved");
    },
    onError: (err) => failToast(err, "Could not save template"),
  });
}

export function useSaveContinueResearchTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (template: string): Promise<string> => {
      const body = z
        .object({ template: z.string() })
        .parse(
          await api("/api/ideas/research/continue-template", {
            method: "PUT",
            json: { template },
          }),
        );
      return body.template;
    },
    onSuccess: (continue_template) => {
      qc.setQueryData(
        ["ideas", "research", "template"],
        (prev: { template: string; continue_template: string } | undefined) =>
          prev
            ? { ...prev, continue_template }
            : { template: "", continue_template },
      );
      toast.success("Continue research template saved");
    },
    onError: (err) => failToast(err, "Could not save continue template"),
  });
}

export function useCreateResearchCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      question: string;
      active?: boolean;
    }): Promise<ResearchCategory> => {
      const body = z
        .object({ category: ResearchCategorySchema })
        .parse(
          await api("/api/ideas/research/categories", {
            method: "POST",
            json: input,
          }),
        );
      return body.category;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ideas", "research", "categories"] });
      toast.success("Category added");
    },
    onError: (err) => failToast(err, "Could not add category"),
  });
}

export function useUpdateResearchCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      active?: boolean;
      question?: string;
      name?: string;
    }): Promise<ResearchCategory> => {
      const { id, ...patch } = input;
      const body = z
        .object({ category: ResearchCategorySchema })
        .parse(await api(`/api/ideas/research/categories/${id}`, { method: "PATCH", json: patch }));
      return body.category;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ideas", "research", "categories"] });
      void qc.invalidateQueries({ queryKey: ["ideas"] });
    },
    onError: (err) => failToast(err, "Could not update category"),
  });
}

export function startIdeaFromEntity(
  ensure: ReturnType<typeof useEnsureIdea>,
  entityType: IdeaEntityType,
  entityId: string,
  title?: string,
) {
  ensure.mutate({
    entity_type: entityType,
    entity_id: entityId,
    ...(title ? { title } : {}),
  });
}
